import { useMutation, useQuery } from "@apollo/client";
import React, { useState } from "react";
import { FEE_EARNER_LEVELS_QUERY, ORGANISATIONAL_UNITS_QUERY } from "../../../graphql/query";

import styles from "./hierarchy-container.css";
import { HierarchyPage } from "./hierarchy-page";
import { UPDATE_ORGANISATIONAL_UNITS } from "../../../graphql/mutation/update-organisational-units";
import { UPDATE_FEE_EARNER_LEVELS } from "../../../graphql/mutation/update-fee-earner-levels";
import { mergeArraysOfObjectsByProp } from "../../../helpers/merge-arrays-of-objects-by-prop";
import { orderBy } from "lodash";

const { ["container"]: containerClassName } = styles as any;

type UpdatePayload = {
  id: number;
  order: number;
};

type StateType = {
  feeEarnerLevels: UpdatePayload[];
  organisationalUnits: UpdatePayload[];
};

export const HierarchySettingsContainer = () => {
  const { data: feeEarnerLevels } = useQuery(FEE_EARNER_LEVELS_QUERY);
  const { data: practiceAreas } = useQuery(ORGANISATIONAL_UNITS_QUERY);

  const [updatePayloads, setUpdatePayloads] = useState<StateType>({
    feeEarnerLevels: [],
    organisationalUnits: [],
  });

  const [updateOrgUnitsMutation] = useMutation(UPDATE_ORGANISATIONAL_UNITS);

  const [updateFeeEarnerLevelsMutation] = useMutation(UPDATE_FEE_EARNER_LEVELS);

  const updateFeeEarnerLevels = (data: { id: number; order: number }[]) => {
    setUpdatePayloads((prev) => ({ ...prev, feeEarnerLevels: data }));
  };

  const updatePracticeAreas = (data: { id: number; order: number }[]) => {
    setUpdatePayloads((prev) => ({ ...prev, organisationalUnits: data }));
  };

  const onSubmit = (tabKey: string) => {
    tabKey === "1"
      ? updateFeeEarnerLevelsMutation({
          variables: {
            input: updatePayloads.feeEarnerLevels.map((x) => ({
              id: x.id,
              payload: { order: { set: x.order } },
            })),
          },
          update: (cache, { data }) => {
            const feeEarnerLevels = data?.updateFeeEarnerLevels;
            if (feeEarnerLevels) {
              cache.writeQuery({
                query: FEE_EARNER_LEVELS_QUERY,
                data: {
                  feeEarnerLevels: {
                    records: feeEarnerLevels,
                  },
                },
              });
            }
          },
        })
      : updateOrgUnitsMutation({
          variables: {
            input: updatePayloads.organisationalUnits.map((x) => ({
              id: x.id,
              payload: { order: { set: x.order } },
            })),
          },
          update: (cache, { data }) => {
            const organisationalUnits = data?.updateOrganisationalUnits;
            if (organisationalUnits) {
              cache.updateQuery(
                {
                  query: ORGANISATIONAL_UNITS_QUERY,
                },
                (prev) => {
                  if (prev) {
                    const updatedOrganisationalUnits = mergeArraysOfObjectsByProp(
                      prev.organisationalUnits,
                      organisationalUnits,
                      "id"
                    );
                    return {
                      organisationalUnits: orderBy(updatedOrganisationalUnits, "order"),
                    };
                  }
                }
              );
            }
          },
        });
  };

  const convertedPracticeAreas = practiceAreas?.organisationalUnits.map((x) => {
    const parent = (x.parent?.id && { ...x.parent, description: x.parent.name }) || undefined;
    const children = x.children?.map((child) => ({ ...child, description: child.name })) || [];
    const maybeWithParent = {
      ...x,
      description: x.name,
      parent: parent ? parent : undefined,
      children,
    };

    return maybeWithParent;
  });

  return (
    <div className={containerClassName}>
      <HierarchyPage
        onSubmit={onSubmit}
        updateFeeEarnerLevels={updateFeeEarnerLevels}
        updatePracticeAreas={updatePracticeAreas}
        feeEarnerLevels={feeEarnerLevels?.feeEarnerLevels.records}
        practiceAreas={convertedPracticeAreas}
      ></HierarchyPage>
    </div>
  );
};
